import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type CoveragesGroupStatsFilters,
  type CoverRequestsGroupHistory,
  type CoverRequestsGroupHistoryInput,
  CoverRequestsGroupingCriteria,
  type DateRange,
  daySinceEpoch,
  DynamicStats,
  HistoricDynamicStatsType,
  type InsistencesGroupHistory,
  type InsistencesGroupHistoryInput,
  InsistencesGroupingCriteria,
} from 'types'

import { queryHistoricDynamicStats } from '@api/dynamic-stats/dynamic-stats.queries'

import DateRangeFilterDialog from '@/components/filters/date-range-filter-dialog'
import {
  useDynamicStats,
  useDynamicStatsOldestComputedAt,
} from '@/stores/dynamic-stats.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import {
  type AnalysisFilters,
  analysisFiltersSchema,
} from '../-components/analysis-filters.schema'
import AnalysisRightBar from '../-components/analysis-right-bar'
import SectionHeader from '../../-components/section-layout/section-header'
import CoverRequestDelayGraph from './-components/cover-request-delay-graph'
import CoverageRateByInsurerGraph from './-components/coverage-rate-by-insurer-graph'
import InsistenceDecisionDelayGraph from './-components/insistence-decision-delay-graph'
import InsistencesCountGraph from './-components/insistences-count-graph'
import InsurersCards from './-components/insurers-cards'

// 3 months
const DEFAULT_DATE_RANGE: DateRange = {
  from: daySinceEpoch(new Date()) - 90,
  to: daySinceEpoch(new Date()),
}

export const Route = createFileRoute('/app/analysis/insurers/')({
  component: RouteComponent,
  validateSearch: analysisFiltersSchema,
})

function RouteComponent() {
  const { t } = useTranslation()

  const navigate = Route.useNavigate()
  const divToExportRef = useRef(null)
  const search = Route.useSearch()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)

  const dateRange = search.dateRange ?? DEFAULT_DATE_RANGE

  const filters: CoveragesGroupStatsFilters = {
    debtorCompanyCountryCodes: search.debtorCompanyCountryCodes,
    groupUuids: search.groupUuids,
    insuranceCompanies: search.insuranceCompanies,
    insuredCompanyCountryCodes: search.insuredCompanyCountryCodes,
    policyUuids: search.policyUuids,
    portfolioUserUuid,
  }

  const displayFiltersWarning = !!Object.values(filters).filter((v) => !!v)
    .length

  const insistencesGroupHistoryInput: InsistencesGroupHistoryInput = {
    filters,
    items: ['none', InsistencesGroupingCriteria.ByInsuranceCompany],
    intervals: { dateRange },
  }

  const insistencesGroupHistoryResponse = useDynamicStats(
    DynamicStats.InsistencesGroupHistory,
    insistencesGroupHistoryInput,
  )
  const insistencesGroupHistory: InsistencesGroupHistory | undefined =
    insistencesGroupHistoryResponse?.state === 'resolved'
      ? insistencesGroupHistoryResponse.data
      : undefined

  const { data: historicCoveragesStatsByInsuranceCompany } = useQuery({
    ...queryHistoricDynamicStats({
      dateRange,
      type: HistoricDynamicStatsType.CoveragesStatsByInsuranceCompany,
    }),
    enabled: !!dateRange,
  })

  const coverRequestsGroupHistoryInput: CoverRequestsGroupHistoryInput = {
    filters,
    items: ['none', CoverRequestsGroupingCriteria.ByInsuranceCompany],
    intervals: { dateRange },
  }

  const coverRequestsGroupHistoryResponse = useDynamicStats(
    DynamicStats.CoverRequestsGroupHistory,
    coverRequestsGroupHistoryInput,
  )
  const coverRequestsGroupHistory: CoverRequestsGroupHistory | undefined =
    coverRequestsGroupHistoryResponse?.state === 'resolved'
      ? coverRequestsGroupHistoryResponse.data
      : undefined

  const lastUpdate = useDynamicStatsOldestComputedAt([
    insistencesGroupHistoryResponse,
    coverRequestsGroupHistoryResponse,
  ])

  const setFilters = (filters: AnalysisFilters) =>
    navigate({ search: (prev) => ({ ...prev, ...filters }) })

  const setDateRangeFilter = (dateRange: DateRange | undefined) =>
    navigate({ search: (prev) => ({ ...prev, dateRange }) })

  return (
    <div className="flex grow flex-col divide-y">
      <SectionHeader
        isSecondary
        title={t('analysis.insurers.title')}
        subtitle={lastUpdate}
      >
        <AnalysisRightBar
          divToExportRef={divToExportRef}
          filters={search}
          setFilters={setFilters}
          filename="coverages-analysis"
        >
          <DateRangeFilterDialog
            value={search.dateRange}
            onChange={setDateRangeFilter}
            className="max-w-56"
          />
        </AnalysisRightBar>
      </SectionHeader>
      <div
        className="flex min-w-0 flex-col gap-2 overflow-x-hidden overflow-y-auto p-2"
        ref={divToExportRef}
      >
        <InsurersCards dateRange={dateRange} />
        <CoverRequestDelayGraph history={coverRequestsGroupHistory} />
        <InsistenceDecisionDelayGraph history={insistencesGroupHistory} />
        <div className="flex w-full min-w-0 flex-col gap-2 2xl:flex-row 2xl:items-stretch">
          <InsistencesCountGraph history={insistencesGroupHistory} />
          <CoverageRateByInsurerGraph
            insurerData={historicCoveragesStatsByInsuranceCompany}
            displayFiltersWarning={displayFiltersWarning}
          />
        </div>
      </div>
    </div>
  )
}
